import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import {
    FileEntry,
    useGetUploadSessionQuery,
    useStartUploadSessionMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import {
    FileEntryStatus,
    UpdateFileBulkFunction,
    UpdateFileFunction,
    useImageModalContext,
} from './ImageSelectModal';
import { ImageModalFooter, ImageModalHeader, ShowSingleFileInput } from './SharedComponents';
import { useImageInput } from '@hooks/files/useImageInput';
import { useFileTypeImage } from '@components/file/fileUtilities';
import { Button } from '@skipper/ui';

export function UploadMobile({
    toggle,
    updater,
    onFinished,
}: {
    toggle: any;
    updater: UpdateFileFunction;
    onFinished?: UpdateFileBulkFunction;
}) {
    const [user] = useUserSession();
    const { sessionId, setSessionId, authorizer, fileCreate, isImmediateUpdate, isMultiFile } =
        useImageModalContext();
    const { fileInput, crop, handlers } = useImageInput({
        immediateUpdate: isImmediateUpdate,
        updateNow: (file) => {
            fileCreate.mutate({
                title: file.name ?? `file-${Date.now()}`,
                file: file,
                authorizerItem: authorizer,
            });
        },
        multiple: isMultiFile,
    });
    const selectIcon = useFileTypeImage();
    const startSession = useStartUploadSessionMutation(getClient('files'), {
        onSettled: (data) => {
            if (data?.startUploadSession?.success) {
                setSessionId(data?.startUploadSession?.uploadSession?.id || '');
            }
        },
    });

    useEffect(() => {
        if (!sessionId) {
            startSession.mutate({
                authorizerItem: {
                    id: user?.id,
                    type: 'User',
                },
            });
        }
    }, [sessionId, user?.id]);

    const uploads = useGetUploadSessionQuery(
        getClient('files'),
        { uploadSessionId: sessionId },
        {
            onSettled: (data) => {
                // Polling for new Files that have uploaded.
                // Multiple Uploads?
            },
            enabled: !!sessionId,
            refetchInterval: 2000,
        },
    );

    const files = (uploads?.data?.getUploadSession?.uploadedFiles || []).slice(
        0,
        isMultiFile ? undefined : 1,
    );

    const onSubmit = useCallback(async () => {
        if (onFinished)
            onFinished(
                files.map((file) => ({
                    id: file.id,
                    file: file as FileEntry,
                    title: file.title,
                    status: FileEntryStatus.Success,
                })),
            );
        else files.forEach((file) => updater(file.id, file as FileEntry));

        toggle(false);
    }, [files]);

    return (
        <div className="relative flex flex-col flex-1 overflow-hidden">
            <div>
                <ImageModalHeader onClick={() => toggle(false)} text="Upload with QR Code" />
            </div>
            <div className="flex flex-col flex-1 h-full w-full justify-center items-center gap-y-6 text-gray-600 text-base font-[400]">
                {files.length > 0 && (
                    <div className="grid grid-cols-1 w-full h-full">
                        <div className="col-span-1 flex flex-1 flex-col items-center justify-center pt-6">
                            {/* NOTE - commenting this out for now as we don't do QR upload cropping (per NEXT-745) */}
                            {/*{file?.fileType?.startsWith('image') && (*/}
                            {/*    <AnimatedImageCropper*/}
                            {/*        selected={{*/}
                            {/*            url: file?.url || '',*/}
                            {/*            title: file?.title || `image-${Date.now()}`,*/}
                            {/*        }}*/}
                            {/*        handlers={handlers}*/}
                            {/*        crop={crop}*/}
                            {/*        close={close}*/}
                            {/*        onClickHeader={() => toggle(false)}*/}
                            {/*    />*/}
                            {/*)}*/}
                            {
                                /*!file?.fileType?.startsWith('image') && */
                                files.map((file, idx) => (
                                    <ShowSingleFileInput
                                        key={idx}
                                        fileInput={{
                                            file: {
                                                name: file.originalFileName,
                                                fileType: file.fileType,
                                            } as any,
                                            localUrl: '',
                                        }}
                                        selectIcon={selectIcon}
                                    />
                                ))
                            }
                        </div>
                    </div>
                )}
                {files.length == 0 && (
                    <React.Fragment>
                        <p>Scan the QR code below with your mobile camera and follow the link:</p>
                        <QRCodeURL sessionId={sessionId} isMultiFile={isMultiFile} />
                        <p className=" whitespace-pre-wrap text-center">
                            {`The uploaded image will appear here as soon as you complete the\nupload on your mobile device.`}
                        </p>
                    </React.Fragment>
                )}
            </div>
            <ImageModalFooter>
                {files.length > 0 && (
                    <div className="flex items-center justify-end pr-3 gap-x-3 bg-white h-full py-3">
                        <Button
                            size="large"
                            type="button"
                            disabled={fileCreate.isLoading}
                            onClick={onSubmit}
                        >
                            Save
                        </Button>
                    </div>
                )}
            </ImageModalFooter>
        </div>
    );
}

const QRCodeComponent: any = QRCode;

function QRCodeURL({ sessionId, isMultiFile }: { sessionId?: string; isMultiFile?: boolean }) {
    const [baseUploadUrl, setBaseUpload] = useState('');
    useEffect(() => {
        if (typeof window === 'undefined') return;
        setBaseUpload(window?.location?.origin || '');
    }, []);
    const url = `${baseUploadUrl}/upload/mobile?sessionId=${sessionId}${
        isMultiFile ? '&multiple=true' : ''
    }`;

    return (
        <div className="w-[150px] h-[150px]">
            {sessionId && (
                <>
                    <QRCodeComponent
                        size={256}
                        style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                        value={url}
                        viewBox={`0 0 256 256`}
                    />
                    {process.env.NODE_ENV === 'development' && (
                        <div className={'text-center'}>
                            <a
                                href={url}
                                target="_blank"
                                className="text-blue-600 underline text-sm"
                                rel="noreferrer"
                            >
                                [dev] Open Link
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
